import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

/**
 * ShimmerButton — premium CTA with a slow continuous shimmer that sweeps
 * across the button background while it's idle. On hover the shimmer
 * speeds up; on click the button depresses with a spring tap.
 *
 * Used as the primary CTA on WelcomeScreen (Commencer), CompletionScreen
 * (Accéder à mon dashboard), First Login Tour final step (Commence par
 * analyser un call).
 *
 * Respects `prefers-reduced-motion` — shimmer stops, tap feedback remains.
 *
 * Usage:
 *   <ShimmerButton onClick={...}>
 *     Commencer
 *     <ArrowRight weight="bold" className="h-4 w-4" />
 *   </ShimmerButton>
 */
interface ShimmerButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  /** Override gradient stops. Defaults to BetterClose purple-violet. */
  gradientFrom?: string
  gradientTo?: string
}

export default function ShimmerButton({
  children,
  /** @deprecated — ShimmerButton now ships with a flat solid base per
   *  the 2026-04-18 anti-AI-slop direction. Props kept for backward
   *  compat but ignored; override via `className` if a consumer really
   *  needs a different colour. */
  gradientFrom: _gradientFrom,
  gradientTo: _gradientTo,
  className = '',
  ...rest
}: ShimmerButtonProps) {
  const reducedMotion = useReducedMotion()
  void _gradientFrom; void _gradientTo

  return (
    <motion.button
      {...(rest as Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'>)}
      whileTap={reducedMotion ? undefined : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 420, damping: 26 }}
      className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-[var(--bn-radius-action)] bg-[var(--bn-accent)] hover:bg-[var(--bn-accent-hover)] border border-[var(--bn-accent-border-medium)] px-6 py-3.5 text-sm [font-weight:var(--bn-cta-font-weight)] tracking-[-0.005em] text-white shadow-[var(--bn-cta-shadow)] hover:shadow-[var(--bn-cta-shadow-hover)] hover:-translate-y-px active:translate-y-0 transition-[transform,background-color,box-shadow] duration-200 ${className}`}
    >
      {/* Direction A: shimmer is hover-only — the resting CTA reads as a
          calm solid surface; the white-light sweep only triggers on hover.
          Opacity gate hides the band on idle so the keyframes don't
          decorate empty space. The animation itself is a Framer
          backgroundPosition tween so it can re-pickup smoothly when the
          parent re-enters hover. */}
      {!reducedMotion && (
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          style={{
            background:
              'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)',
            backgroundSize: '200% 100%',
          }}
          animate={{ backgroundPosition: ['-50% 0%', '150% 0%'] }}
          transition={{ duration: 2.8, ease: 'linear', repeat: Infinity }}
        />
      )}

      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </motion.button>
  )
}
